import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, wasRecoveryCallback } from '@/lib/supabase';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
/**
 * Two-phase password reset:
 * Phase 1 — user enters email, Supabase sends a reset email.
 * Phase 2 — user clicks the link, lands here with a recovery token in the
 *            URL hash. Supabase JS fires PASSWORD_RECOVERY via onAuthStateChange,
 *            which switches to the new-password form. On submit we call
 *            supabase.auth.updateUser({ password }).
 */
const MIN_PASSWORD_LENGTH = 8;
export default function PasswordReset() {
    const navigate = useNavigate();
    const [phase, setPhase] = useState('request');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const handleToken = useCallback((token) => setCaptchaToken(token), []);
    const handleExpire = useCallback(() => setCaptchaToken(null), []);
    useEffect(() => {
        if (!SITE_KEY || phase !== 'request')
            return;
        if (!document.getElementById('cf-turnstile-script')) {
            const script = document.createElement('script');
            script.id = 'cf-turnstile-script';
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
            script.async = true;
            document.head.appendChild(script);
        }
        let intervalId;
        function tryRender() {
            const ts = window.turnstile;
            if (ts && containerRef.current && !widgetIdRef.current) {
                clearInterval(intervalId);
                const id = ts.render(containerRef.current, {
                    sitekey: SITE_KEY,
                    callback: handleToken,
                    'expired-callback': handleExpire,
                    'error-callback': handleExpire,
                });
                if (id !== undefined)
                    widgetIdRef.current = id;
            }
        }
        intervalId = setInterval(tryRender, 100);
        return () => {
            clearInterval(intervalId);
            const ts = window.turnstile;
            if (ts && widgetIdRef.current) {
                ts.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [phase, handleToken, handleExpire]);
    // Listen for Supabase PASSWORD_RECOVERY event — fires when user lands via
    // the reset link.
    //
    // Race condition: App.tsx holds a loading spinner until useAuth's getSession()
    // resolves. For implicit flow, Supabase fires PASSWORD_RECOVERY synchronously
    // during createClient() — before ANY React effects run — so this component
    // always misses it. By the time we subscribe, only INITIAL_SESSION replays.
    // wasRecoveryCallback is captured in supabase.ts BEFORE createClient() cleans
    // up the URL, so it reliably records whether this page load was a recovery
    // callback regardless of which auth flow Supabase used (PKCE / implicit /
    // token_hash).
    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY') {
                setPhase('new-password');
            }
            else if (event === 'INITIAL_SESSION' && wasRecoveryCallback && session) {
                // PASSWORD_RECOVERY already fired before we subscribed; session is live
                setPhase('new-password');
            }
        });
        return () => subscription.unsubscribe();
    }, []);
    // ── Phase 1: request reset email ──────────────────────────────────────────
    async function handleRequest(e) {
        e.preventDefault();
        setError(null);
        setLoading(true);
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
            redirectTo: `${window.location.origin}/reset-password`,
            ...(SITE_KEY && captchaToken ? { captchaToken } : {}),
        });
        setLoading(false);
        if (resetError) {
            setError(resetError.message);
            return;
        }
        setPhase('sent');
    }
    // ── Phase 2: set new password ─────────────────────────────────────────────
    async function handleSetPassword(e) {
        e.preventDefault();
        setError(null);
        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setLoading(true);
        const { error: updateError } = await supabase.auth.updateUser({ password });
        setLoading(false);
        if (updateError) {
            setError(updateError.message);
            return;
        }
        setPhase('success');
        // Sign out so they log in cleanly with the new password
        await supabase.auth.signOut();
    }
    // ── Render ────────────────────────────────────────────────────────────────
    if (phase === 'sent') {
        return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("div", { className: "flex justify-center mb-6", children: _jsx("img", { src: "/images/logo-v2.webp", alt: "Springfield Dog Centre", className: "w-56" }) }), _jsx("h1", { children: "Check your email" }), _jsxs("p", { children: ["We've sent a password reset link to ", _jsx("strong", { children: email }), ". The link is valid for 1 hour."] }), _jsx("p", { children: _jsx(Link, { to: "/login", children: "Back to log in" }) })] }) }));
    }
    if (phase === 'new-password') {
        return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("div", { className: "flex justify-center mb-6", children: _jsx("img", { src: "/images/logo-v2.webp", alt: "Springfield Dog Centre", className: "w-56" }) }), _jsx("h1", { children: "Set a new password" }), _jsxs("form", { onSubmit: handleSetPassword, noValidate: true, children: [_jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "password", children: "New password" }), _jsx("input", { id: "password", type: "password", autoComplete: "new-password", required: true, value: password, onChange: (e) => setPassword(e.target.value), placeholder: `At least ${MIN_PASSWORD_LENGTH} characters` })] }), _jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "confirm", children: "Confirm new password" }), _jsx("input", { id: "confirm", type: "password", autoComplete: "new-password", required: true, value: confirm, onChange: (e) => setConfirm(e.target.value) })] }), error && _jsx("p", { className: "error", children: error }), _jsx("button", { type: "submit", disabled: loading, children: loading ? 'Saving…' : 'Set new password' })] })] }) }));
    }
    if (phase === 'success') {
        return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("div", { className: "flex justify-center mb-6", children: _jsx("img", { src: "/images/logo-v2.webp", alt: "Springfield Dog Centre", className: "w-56" }) }), _jsx("h1", { children: "Password updated" }), _jsx("p", { children: "Your password has been changed successfully. You can now log in with your new password." }), _jsx("p", { children: _jsx(Link, { to: "/login", children: "Go to log in" }) })] }) }));
    }
    // Phase 1: request
    return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("div", { className: "flex justify-center mb-6", children: _jsx("img", { src: "/images/logo-v2.webp", alt: "Springfield Dog Centre", className: "w-56" }) }), _jsx("h1", { children: "Reset your password" }), _jsx("p", { children: "Enter your email address and we'll send you a reset link." }), _jsxs("form", { onSubmit: handleRequest, noValidate: true, children: [_jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "email", children: "Email address" }), _jsx("input", { id: "email", type: "email", autoComplete: "email", required: true, value: email, onChange: (e) => setEmail(e.target.value) })] }), SITE_KEY && (_jsx("div", { className: "flex justify-center my-4", children: _jsx("div", { ref: containerRef }) })), error && _jsx("p", { className: "error", children: error }), _jsx("button", { type: "submit", disabled: loading || (!!SITE_KEY && !captchaToken), children: loading ? 'Sending…' : 'Send reset link' })] }), _jsx("p", { children: _jsx(Link, { to: "/login", children: "Back to log in" }) })] }) }));
}
